/* Admin Appointments — iter101.19
 *
 * Aligned to /doctor/appointments visual grammar (flat header · 5-tile
 * KPI strip · shared FilterSearchBar + FilterChipRow · day-grouped
 * appointment cards). Kept admin-specific: every card also shows the
 * doctor + clinic prominently, so an admin scanning the list can spot
 * network-wide patterns at a glance.
 *
 * Non-goals: booking / rescheduling from admin (doctors still own the
 * calendar). This page is a read-only network view + status chips.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import {
    Calendar, CalendarBlank, Clock, Phone, Stethoscope, Buildings,
    CheckCircle, Warning, XCircle, ListBullets, ArrowRight, User as UserIcon,
    Hourglass, TrendUp, CalendarPlus, CaretDown,
} from "@phosphor-icons/react";


/* Same status vocabulary as the doctor page so admins learn one grammar. */
const STATUS_STYLE = {
    scheduled:    { label: "Scheduled",    cls: "bg-slate-100 text-slate-700",     tone: "slate" },
    pending:      { label: "Scheduled",    cls: "bg-slate-100 text-slate-700",     tone: "slate" },
    confirmed:    { label: "Confirmed",    cls: "bg-emerald-100 text-emerald-800", tone: "emerald" },
    arrived:      { label: "Arrived",      cls: "bg-amber-100 text-amber-800",     tone: "amber" },
    in_progress:  { label: "In session",   cls: "bg-blue-100 text-blue-800",       tone: "sky" },
    completed:    { label: "Completed",    cls: "bg-blue-50 text-blue-600",        tone: "sky" },
    cancelled:    { label: "Cancelled",    cls: "bg-rose-100 text-rose-700",       tone: "rose" },
    no_show:      { label: "No-show",      cls: "bg-rose-50 text-rose-600",        tone: "rose" },
    rescheduled:  { label: "Rescheduled",  cls: "bg-purple-50 text-purple-600",    tone: "violet" },
};

const APPT_TYPE_LABEL = {
    consultation:    "Consultation",
    scan:            "Scan / impression",
    delivery:        "Aligner delivery",
    adjustment:      "Adjustment / review",
    refinement_scan: "Refinement scan",
    retainer_check:  "Retainer check",
    emergency:       "Emergency / repair",
    video_consult:   "Video consultation",
    first_fitting:   "First fitting",
    review:          "Review",
    refinement:      "Refinement",
    general:         "General visit",
};

const ACTIVE_STATES = new Set(["scheduled", "pending", "confirmed", "arrived", "in_progress"]);

const todayLocalISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const startOfWeekISO = () => {
    const d = new Date();
    const day = d.getDay(); // 0=Sun
    const diff = (day === 0 ? -6 : 1 - day); // Monday as week start
    const monday = new Date(d);
    monday.setDate(d.getDate() + diff);
    return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;
};

const endOfWeekISO = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = (day === 0 ? 0 : 7 - day);
    const sunday = new Date(d);
    sunday.setDate(d.getDate() + diff);
    return `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, "0")}-${String(sunday.getDate()).padStart(2, "0")}`;
};

/* "13:30" → "1:30 PM" — matches the doctor formatter. */
const fmtTime = (hhmm) => {
    if (!hhmm) return "";
    const [hs, ms] = hhmm.split(":");
    let h = parseInt(hs, 10);
    const period = h >= 12 ? "PM" : "AM";
    h = h % 12; if (h === 0) h = 12;
    return `${h}:${ms} ${period}`;
};

/* "2026-08-04" → "Mon · 4 Aug" — day-header caption on grouped cards. */
const fmtDayHeader = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso + "T00:00:00");
        const dow = d.toLocaleDateString(undefined, { weekday: "short" });
        const rest = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
        const today = todayLocalISO();
        const tomorrow = (() => {
            const t = new Date(); t.setDate(t.getDate() + 1);
            return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
        })();
        if (iso === today) return `Today · ${rest}`;
        if (iso === tomorrow) return `Tomorrow · ${rest}`;
        return `${dow} · ${rest}`;
    } catch {
        return iso;
    }
};


function StatTile({ label, value, Icon, tone = "primary", hint, onClick, testid }) {
    const accent = tone === "primary" ? "bg-primary/10 text-primary"
        : tone === "emerald" ? "bg-emerald-100 text-emerald-700"
        : tone === "amber" ? "bg-amber-100 text-amber-700"
        : tone === "rose" ? "bg-rose-100 text-rose-700"
        : tone === "sky" ? "bg-sky-100 text-sky-700"
        : tone === "violet" ? "bg-violet-100 text-violet-700"
        : "bg-primary/10 text-primary";
    const Component = onClick ? "button" : "div";
    return (
        <Component
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`flex items-start gap-3 rounded-xl bg-card border border-border p-3 text-left transition ${onClick ? "hover:border-primary/40 hover:shadow-sm cursor-pointer" : ""}`}
            data-testid={testid}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-1 truncate">{hint}</div>}
            </div>
        </Component>
    );
}


function StatusPill({ status }) {
    const s = STATUS_STYLE[status] || { label: status || "Unknown", cls: "bg-secondary text-foreground" };
    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.cls}`}
            data-testid={`admin-appt-status-${status || "unknown"}`}
        >
            {s.label}
        </span>
    );
}


function AppointmentCard({ a }) {
    const startTime = fmtTime(a.time || a.start_time || a.slot_time);
    const patientName = a.patient_name || "Patient";
    return (
        <div
            className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 py-3 hover:bg-secondary/30 transition"
            data-testid={`admin-appointment-${a.id}`}
        >
            {/* Time */}
            <div className="shrink-0 sm:w-24">
                <div className="font-display text-base font-bold text-foreground tabular-nums">{startTime || "—"}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{APPT_TYPE_LABEL[a.type] || a.type || "Appointment"}</div>
            </div>
            {/* Patient + phone */}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-sm text-foreground truncate">{patientName}</div>
                    <StatusPill status={a.status} />
                </div>
                <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                    <Phone size={10} /> {a.patient_phone || "—"}
                </div>
            </div>
            {/* Doctor + clinic (admin-only) */}
            <div className="min-w-0 sm:w-64">
                <div className="text-[12px] font-medium text-foreground inline-flex items-center gap-1 truncate">
                    <Stethoscope size={11} className="text-primary shrink-0" />
                    {a.doctor_name || "—"}
                </div>
                <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5 truncate">
                    <Buildings size={10} className="shrink-0" />
                    {a.clinic_name || "—"}
                </div>
            </div>
            {/* Action: open patient */}
            <div className="shrink-0 flex items-center gap-2">
                {a.patient_id && (
                    <Link
                        to={`/admin/patients/${a.patient_id}`}
                        className="text-[11px] font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1"
                        data-testid={`admin-appt-open-patient-${a.id}`}
                    >
                        View patient <ArrowRight size={11} />
                    </Link>
                )}
            </div>
        </div>
    );
}


function DayGroup({ dateISO, items }) {
    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`admin-appointments-day-${dateISO}`}>
            <div className="px-4 py-2 bg-secondary/30 border-b border-border flex items-center gap-2">
                <CalendarBlank size={12} className="text-muted-foreground" weight="fill" />
                <div className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">
                    {fmtDayHeader(dateISO)}
                </div>
                <div className="text-[10px] text-muted-foreground ml-auto tabular-nums">
                    {items.length} appointment{items.length === 1 ? "" : "s"}
                </div>
            </div>
            <div className="divide-y divide-border/60">
                {items.map((a) => <AppointmentCard key={a.id} a={a} />)}
            </div>
        </div>
    );
}


export default function AdminAppointments() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("upcoming"); // upcoming | today | past | cancelled | all
    const [loading, setLoading] = useState(true);
    /* iter101.20 — Sort + doctor filter dropdowns to match the doctor
       portal filter-bar grammar. Sort defaults to "" (natural date+time
       ascending order). Doctor filter empty = every provider. */
    const [sortBy, setSortBy] = useState("");
    const [doctorFilter, setDoctorFilter] = useState("");
    /* iter101.22 — Clinic filter for admins running a multi-clinic
       network. Derived from loaded appointments. */
    const [clinicFilter, setClinicFilter] = useState("");

    useEffect(() => {
        api.get("/admin/appointments")
            .then((r) => setItems(r.data || []))
            .finally(() => setLoading(false));
    }, []);

    /* Derived list of unique doctors seen in the current appointments
       roster — populates the doctor filter dropdown. Deduped by name. */
    const doctorOptions = useMemo(() => {
        const map = new Map();
        items.forEach((a) => {
            const id = a.doctor_id;
            const name = a.doctor_name;
            if (id && name && !map.has(id)) map.set(id, name);
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [items]);

    /* iter101.22 — Derived clinic list (unique names, alpha-sorted). */
    const clinicOptions = useMemo(() => {
        const set = new Set();
        items.forEach((a) => {
            const c = (a.clinic_name || "").trim();
            if (c) set.add(c);
        });
        return Array.from(set).sort();
    }, [items]);

    /* KPI STRIP — computed cheaply from the in-memory list. Every tile is
       clickable and drives a matching chip below. */
    const stats = useMemo(() => {
        const today = todayLocalISO();
        const weekStart = startOfWeekISO();
        const weekEnd = endOfWeekISO();
        let todayCount = 0, week = 0, confirmed = 0, pending = 0, cancelled = 0, upcoming = 0;
        items.forEach((a) => {
            const d = a.date || "";
            if (d === today) todayCount += 1;
            if (d >= weekStart && d <= weekEnd) week += 1;
            if (a.status === "confirmed") confirmed += 1;
            if (a.status === "pending" || a.status === "scheduled") pending += 1;
            if (a.status === "cancelled" || a.status === "no_show") cancelled += 1;
            if (d >= today && ACTIVE_STATES.has(a.status)) upcoming += 1;
        });
        return { todayCount, week, confirmed, pending, cancelled, upcoming, total: items.length };
    }, [items]);

    /* Filter (chip) then doctor filter then search — same order as /doctor/appointments. */
    const filtered = useMemo(() => {
        const today = todayLocalISO();
        let list = items;
        if (filter === "upcoming") list = list.filter((a) => (a.date || "") >= today && ACTIVE_STATES.has(a.status));
        else if (filter === "today") list = list.filter((a) => a.date === today);
        else if (filter === "past") list = list.filter((a) => (a.date || "") < today || a.status === "completed");
        else if (filter === "cancelled") list = list.filter((a) => a.status === "cancelled" || a.status === "no_show");
        if (doctorFilter) list = list.filter((a) => a.doctor_id === doctorFilter);
        if (clinicFilter) list = list.filter((a) => (a.clinic_name || "") === clinicFilter);
        if (q.trim()) {
            const l = q.trim().toLowerCase();
            list = list.filter((a) =>
                (a.patient_name || "").toLowerCase().includes(l) ||
                (a.doctor_name || "").toLowerCase().includes(l) ||
                (a.patient_phone || "").includes(q) ||
                (a.clinic_name || "").toLowerCase().includes(l)
            );
        }
        return list;
    }, [items, q, filter, doctorFilter, clinicFilter]);

    /* Group by day + sort each day's list by time (or by patient name
       when sortBy === "name"). Days are always chronological — sortBy
       only reorders WITHIN a day so the grouped-by-day mental model
       stays intact. */
    const groups = useMemo(() => {
        const map = new Map();
        filtered.forEach((a) => {
            const d = a.date || "unknown";
            if (!map.has(d)) map.set(d, []);
            map.get(d).push(a);
        });
        const entries = Array.from(map.entries());
        const isPastView = filter === "past" || filter === "cancelled";
        entries.sort((a, b) => (isPastView ? b[0].localeCompare(a[0]) : a[0].localeCompare(b[0])));
        entries.forEach(([, list]) => {
            if (sortBy === "name") {
                list.sort((x, y) => (x.patient_name || "").localeCompare(y.patient_name || ""));
            } else if (sortBy === "doctor") {
                list.sort((x, y) => (x.doctor_name || "").localeCompare(y.doctor_name || ""));
            } else {
                list.sort((x, y) => (x.time || "").localeCompare(y.time || ""));
            }
        });
        return entries;
    }, [filtered, filter, sortBy]);

    const chips = [
        { id: "upcoming",  label: "Upcoming",  count: stats.upcoming,   tone: "primary" },
        { id: "today",     label: "Today",     count: stats.todayCount, tone: "emerald" },
        { id: "past",      label: "Past",      count: 0,                tone: "slate" },
        { id: "cancelled", label: "Cancelled", count: stats.cancelled,  tone: "rose" },
        { id: "all",       label: "All",       count: stats.total,      tone: "violet" },
    ];

    return (
        <div data-testid="admin-appointments-page" className="space-y-6">
            {/* HEADER — flat, matches /doctor/appointments */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Work · Appointments</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Appointments</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Every booking across the network. Filter, search, or open a patient card to drill in.
                    </p>
                </div>
            </div>

            {/* KPI STRIP */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <StatTile
                    label="Today"       value={stats.todayCount} Icon={Calendar}     tone="primary"
                    hint={stats.todayCount > 0 ? "Book-ins today" : "Quiet day"}
                    onClick={() => setFilter("today")}
                    testid="admin-appts-kpi-today"
                />
                <StatTile
                    label="This week"   value={stats.week}       Icon={CalendarPlus} tone="sky"
                    hint="Mon → Sun window"
                    onClick={() => setFilter("upcoming")}
                    testid="admin-appts-kpi-week"
                />
                <StatTile
                    label="Confirmed"   value={stats.confirmed}  Icon={CheckCircle}  tone="emerald"
                    hint="Ready for the chair"
                    testid="admin-appts-kpi-confirmed"
                />
                <StatTile
                    label="Pending"     value={stats.pending}    Icon={Hourglass}    tone="amber"
                    hint={stats.pending > 0 ? "Awaiting confirmation" : "All caught up"}
                    testid="admin-appts-kpi-pending"
                />
                <StatTile
                    label="Cancelled"   value={stats.cancelled}  Icon={XCircle}      tone="rose"
                    hint={stats.cancelled > 0 ? "Includes no-shows" : "None"}
                    onClick={() => setFilter("cancelled")}
                    testid="admin-appts-kpi-cancelled"
                />
            </div>

            {/* FILTER STRIP — matches /doctor/patients pattern exactly:
                search + Sort dropdown + Doctor filter dropdown + no view
                toggle (admin list is intentionally the only view). */}
            <div className="flex flex-col gap-3">
                <FilterSearchBar
                    query={q}
                    setQuery={setQ}
                    placeholder="Search patient, doctor, clinic…"
                    searchTestid="admin-appointments-search"
                    testid="admin-appointments-filter-bar"
                    rightSlot={(
                        <>
                            <div className="relative">
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                                    data-testid="admin-appointments-sort-select"
                                    aria-label="Sort appointments"
                                >
                                    <option value="">Sort</option>
                                    <option value="name">Patient A–Z</option>
                                    <option value="doctor">Doctor A–Z</option>
                                </select>
                                <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                            </div>
                            <div className="relative">
                                <select
                                    value={doctorFilter}
                                    onChange={(e) => setDoctorFilter(e.target.value)}
                                    className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer max-w-[180px] truncate"
                                    data-testid="admin-appointments-doctor-filter"
                                    aria-label="Filter by doctor"
                                >
                                    <option value="">All doctors</option>
                                    {doctorOptions.map((d) => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                                <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                            </div>
                            <div className="relative">
                                <select
                                    value={clinicFilter}
                                    onChange={(e) => setClinicFilter(e.target.value)}
                                    className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer max-w-[180px] truncate"
                                    data-testid="admin-appointments-clinic-filter"
                                    aria-label="Filter by clinic"
                                >
                                    <option value="">All clinics</option>
                                    {clinicOptions.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                                <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                            </div>
                        </>
                    )}
                />
                <FilterChipRow
                    chips={chips}
                    value={filter}
                    onChange={setFilter}
                    testid="admin-appointments-chip-row"
                    chipTestidPrefix="admin-appointments-filter"
                />
            </div>

            {/* BODY */}
            {loading ? (
                <div className="text-muted-foreground text-sm text-center py-10" data-testid="admin-appointments-loading">
                    Loading appointments…
                </div>
            ) : groups.length === 0 ? (
                <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="admin-appointments-empty">
                    <Calendar size={28} className="mx-auto text-muted-foreground mb-3" />
                    <div className="font-display text-lg font-bold">
                        {q || filter !== "upcoming" ? "No appointments match" : "No upcoming appointments"}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">
                        {q || filter !== "upcoming"
                            ? "Try clearing your search or switching to a different filter."
                            : "Once doctors start booking on the network, everything shows up here."}
                    </p>
                    {(q || filter !== "upcoming") && (
                        <Button
                            variant="outline"
                            onClick={() => { setQ(""); setFilter("upcoming"); }}
                            className="rounded-full mt-4"
                            data-testid="admin-appointments-clear-filters"
                        >
                            Clear filters
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {groups.map(([date, list]) => (
                        <DayGroup key={date} dateISO={date} items={list} />
                    ))}
                </div>
            )}
        </div>
    );
}
